export type DisplayFormat = 'currency' | 'percentage' | 'privacy' | 'r-multiple';

/**
 * Formate une valeur selon le format d'affichage sélectionné
 */
export function formatValue(
  value: number,
  format: DisplayFormat,
  options?: {
    currency?: string;
    initialBalance?: number;
    averageRiskPercent?: number;
    showSign?: boolean;
    compact?: boolean;
    isBalance?: boolean;
  }
): string {
  const { currency = '$', initialBalance = 10000, averageRiskPercent = 1.5, showSign = false, compact = false, isBalance = false } = options || {};

  switch (format) {
    case 'currency':
      // Format monétaire standard
      const sign = showSign && value > 0 ? '+' : '';
      const decimals = compact ? 0 : 2;
      return `${sign}${currency}${value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      })}`;

    case 'percentage':
      // Format pourcentage basé sur le capital initial
      let percentage: number;
      if (isBalance) {
        // Si c'est un solde, calculer le gain/perte par rapport au solde initial
        percentage = initialBalance > 0 ? ((value - initialBalance) / initialBalance) * 100 : 0;
      } else {
        // Si c'est un profit/perte, calculer directement le pourcentage
        percentage = initialBalance > 0 ? (value / initialBalance) * 100 : 0;
      }
      const percentSign = showSign && percentage > 0 ? '+' : '';
      const percentDecimals = compact ? 1 : 2;
      return `${percentSign}${percentage.toFixed(percentDecimals)}%`;

    case 'privacy':
      // Mode privé - masque les valeurs
      return '***';

    case 'r-multiple':
      // Format R multiple basé sur le risque moyen par trade
      // Le risque moyen en $ = (risque % / 100) * capital initial
      const averageRiskAmount = (averageRiskPercent / 100) * initialBalance;
      if (averageRiskAmount === 0) return '0R';
      
      let rValue: number;
      if (isBalance) {
        // Si c'est un solde, calculer le gain/perte par rapport au solde initial
        rValue = value - initialBalance;
      } else {
        // Si c'est un profit/perte direct
        rValue = value;
      }
      
      const rMultiple = rValue / averageRiskAmount;
      const rSign = showSign && rMultiple > 0 ? '+' : '';
      const rDecimals = compact ? 1 : 2;
      return `${rSign}${rMultiple.toFixed(rDecimals)}R`;

    default:
      return value.toString();
  }
}

/**
 * Formate un pourcentage selon le format d'affichage
 */
export function formatPercentage(
  percentage: number,
  hideValue: boolean = false,
  options?: {
    showSign?: boolean;
    decimals?: number;
  }
): string {
  if (hideValue) {
    return '***';
  }

  const { showSign = true, decimals = 2 } = options || {};
  const sign = showSign && percentage > 0 ? '+' : '';
  return `${sign}${percentage.toFixed(decimals)}%`;
}

/**
 * Formate un nombre (sans devise) selon le format d'affichage
 */
export function formatNumber(
  value: number,
  format: DisplayFormat,
  options?: {
    decimals?: number;
  }
): string {
  const { decimals = 0 } = options || {};

  switch (format) {
    case 'privacy':
      return '***';
      
    case 'currency':
    case 'percentage':
    case 'r-multiple':
    default:
      return value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
  }
}

/**
 * Vérifie si le format nécessite de masquer les valeurs
 */
export function isPrivacyMode(format: DisplayFormat): boolean {
  return format === 'privacy';
}

